import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Link as ReactRouterLink,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSlug from "rehype-slug";
import CodeMirror from "@uiw/react-codemirror";
import { markdown as markdownLanguage } from "@codemirror/lang-markdown";
import { Search, FileText, History, X, ArrowLeft, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";
import { Card, Label } from "@/design/primitives";
import {
  fetchDocs,
  fetchDoc,
  fetchDocHistory,
  fetchDocIndex,
  fetchDocBrands,
  fetchDocBacklinks,
  fetchDocAnnotations,
  createDocAnnotation,
  updateDocAnnotation,
  replyDocAnnotation,
  postDocEdit,
  fetchDocEdits,
  postDocEditCancel,
} from "@/lib/ccApi";
import PdfViewer, { ThreadPanel, repliesOf } from "./PdfViewer";
import { remarkDocLinks, docIdFromUrl } from "./remarkDocLinks";
import { openInDocs } from "@/lib/docLinkify";
import "./docSurface.css";

// P3.4 (DOC-ANNOT-CONTRACT.md) adds the annotation layer over the reader:
// md selections become quote-anchored notes (affordance → composer →
// highlight through a remark plugin), stale/moved quotes list above the
// content, and the same ThreadPanel the PDF boxes use serves the threads.

// The reader at /docs — the workspace's own documents, searchable and
// readable inside the OS.
//
// P1: it shows a document, the document's history, and a PDF. There is no
// PDF engine in the browser: the PDF is served by the backend and drawn by
// the browser's own viewer in a plain iframe.
//
// P2 adds the editor: an md document offers an Edit button, a Save that is
// a REQUEST (see the chip below), a Cancel, and an Edit chip (testid
// "docs-edit-chip") whose state line names the edit's stage in the screen's
// own words — "queued", "writing to your Mac", "applied", or the conflict /
// failed / cancelled word — followed by the backend's detail on a second
// line when there is one. Save posts content + base_sha8 (the sha8 of the
// exact text the editor opened, from GET /api/docs/(doc_id)'s
// content_sha8); a stale draft lands as a conflict and the banner offers
// "Reload" beside "Your edit is preserved". While an edit is in flight the
// reader polls GET /api/docs/(doc_id)/edits every 5 seconds (and once
// immediately after Save); "applied" reloads the document and its history
// with a calm toast; "cancelled" is a calm toast. Cancel — including while
// the edit is already "applying", where the backend may answer 409 — posts
// POST /api/docs/edits/(edit_id)/cancel, stops the poll, and leaves the
// reader on the document without touching the draft.
//
// The lane that feeds this (SYSTEM/kit-os/mirror/doc-sync.py) pushes the
// workspace's markdown and PDFs; the git history is computed THERE, because
// the server has no git and no durable disk.

// The lanes the list groups by. The lane is the first folder of the path,
// so this list is the set of top-level folders the workspace actually has.
const LANE_ORDER = ["Kit", "SYSTEM", "skills", "memory", "projects", "other"];

const laneLabel = (lane) => {
  if (lane === "Kit") return "Kit & life";
  if (lane === "SYSTEM") return "System";
  if (lane === "skills") return "Skills";
  if (lane === "memory") return "Memory";
  if (lane === "projects") return "Projects";
  return lane;
};

const sortLanes = (lanes) => {
  const keys = Object.keys(lanes || {});
  return keys.sort((a, b) => {
    const ia = LANE_ORDER.indexOf(a);
    const ib = LANE_ORDER.indexOf(b);
    if (ia === -1 && ib === -1)
      return lanes[b] - lanes[a] || a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
};

const stampText = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime()))
    return String(iso).slice(0, 16).replace("T", " ");
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const bytes = (n) => {
  const v = Number(n) || 0;
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${Math.round(v / 1024)} KB`;
  return `${(v / (1024 * 1024)).toFixed(1)} MB`;
};

// ── the edit chip (P2) ────────────────────────────────────────────────────
// The words the chip says. The state word is the screen's own English; the
// detail line underneath is the backend's own words (never a code).
const EDIT_STATE_WORD = {
  queued: "queued",
  applying: "writing to your Mac",
  applied: "applied",
  conflict: "conflict",
  failed: "failed",
  cancelled: "cancelled",
  rejected: "rejected",
};
const EDIT_STATE_TONE = {
  queued: "muted",
  applying: "muted",
  applied: "green",
  conflict: "amber",
  failed: "red",
  cancelled: "muted",
  rejected: "muted",
};

// An md edit row from GET /api/docs/(id)/edits: which one matters? An
// "in flight" row is queued or applying — the life the chip watches. A
// settled row may arrive alone (polls race the Save), so a settled row is
// accepted once it is fresh for this document and nothing else supersedes
// it. Rows newest first, per the backend's contract.
const activeEditOf = (rows) => {
  const list = Array.isArray(rows) ? rows : rows?.items || [];
  const inflight = list.find(
    (r) => r && (r.state === "queued" || r.state === "applying"),
  );
  if (inflight) return inflight;
  return (
    list.find(
      (r) => r && r.state && r.state !== "queued" && r.state !== "applying",
    ) || null
  );
};

// ── the list ──────────────────────────────────────────────────────────────

// P3.2 Phase B: a brand chip's testid encodes the brand's own name — lower-
// cased, spaces folded to dashes, everything outside [a-z0-9-] stripped.
const brandChipId = (name) =>
  String(name || "")
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");

// One rail row, shared by the main list and the contextual sections — the
// same click handler everywhere, so any row opens its doc exactly the way
// the main list does. `reading` marks the open document with a subtle amber
// left border and dot (testid docs-reading-now); contextual rows get their
// own testid (docs-sibling-… / docs-recent-…).
const DocRow = ({ it, onOpen, testid, reading = false }) => (
  <button
    type="button"
    onClick={() => onOpen(it)}
    data-testid={testid}
    className={cn(
      "flex w-full items-start gap-2.5 border-l-2 border-transparent px-4 py-2 text-left hover:bg-os-wash",
      reading && "border-l-os-amber",
    )}
  >
    <FileText
      className="mt-0.5 h-3.5 w-3.5 shrink-0 text-os-ink-4"
      strokeWidth={1.75}
    />
    <span className="min-w-0 flex-1">
      <span className="block truncate text-[0.875rem] text-os-ink">
        {it.title}
      </span>
      <span className="mt-0.5 block truncate font-mono text-[0.625rem] text-os-ink-4">
        {it.path}
      </span>
      {it.snippet && (
        <span className="mt-1 block line-clamp-2 text-[0.75rem] leading-[1.4] text-os-ink-2">
          {it.snippet}
        </span>
      )}
    </span>
    {reading && (
      <span
        data-testid="docs-reading-now"
        title="Reading now"
        className="mt-1.5 shrink-0"
      >
        <span className="block h-1.5 w-1.5 rounded-full bg-os-amber" />
      </span>
    )}
    {it.hasPdf && (
      <span className="mt-0.5 shrink-0 rounded-chip border border-os-border px-1.5 py-0.5 font-mono text-[0.5625rem] uppercase tracking-[0.08em] text-os-ink-4">
        pdf
      </span>
    )}
  </button>
);

const DocList = ({
  items,
  lanes,
  activeLane,
  onLane,
  activeBrand,
  onBrand,
  query,
  onQuery,
  onOpen,
  loading,
  error,
  total,
  brands = [],
  openId = null,
  siblings = [],
  recent = [],
}) => {
  const grouped = useMemo(() => {
    const byLane = new Map();
    for (const it of items) {
      const key = it.lane || "other";
      if (!byLane.has(key)) byLane.set(key, []);
      byLane.get(key).push(it);
    }
    return byLane;
  }, [items]);

  // The contextual sections only exist while a doc is open, and drop away
  // whenever the search box speaks — a search is a deliberate narrowing, and
  // context would just be noise on top of it.
  const contextual = Boolean(openId) && !query;

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="docs-list">
      <div className="flex items-center gap-2 border-b border-os-border px-4 py-3">
        <div className="relative flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-os-ink-4"
            strokeWidth={1.75}
          />
          <input
            type="search"
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            placeholder="Search every document"
            aria-label="Search documents"
            data-testid="docs-search"
            className="h-9 w-full rounded-control border border-os-border bg-white pl-9 pr-3 text-[0.875rem] text-os-ink outline-none placeholder:text-os-ink-4 focus:border-os-ink-3"
          />
        </div>
      </div>

      {/* P3.2 Phase B (DOC-P32-CONTRACT.md): the brand hubs. One chip per
          brand from GET /api/docs/brands — {name, count, pdfCount}, backend
          sorted (count desc, name asc) — ABOVE the lane groups. A brand is a
          single-select filter: clicking an active chip clears it, "All docs"
          clears from anywhere. While a brand is active the list fetch carries
          ?brand= and the chip stays visibly active; the row survives a search
          (the chips describe the whole library, not the filtered slice). */}
      {brands.length > 0 && (
        <div
          className="flex flex-wrap items-center gap-1.5 border-b border-os-border px-4 py-2.5"
          data-testid="docs-brand-chips"
        >
          <button
            type="button"
            onClick={() => onBrand("")}
            data-testid="docs-brand-chip-all"
            className={cn(
              "rounded-chip border px-2.5 py-1 font-mono text-[0.625rem] uppercase tracking-[0.08em]",
              !activeBrand
                ? "border-os-ink-3 bg-os-wash text-os-ink"
                : "border-os-border text-os-ink-3 hover:bg-os-wash",
            )}
          >
            All docs
          </button>
          {brands.map((b) => {
            const encoded = brandChipId(b.name);
            return (
              <button
                type="button"
                key={b.name}
                onClick={() => onBrand(activeBrand === b.name ? "" : b.name)}
                data-testid={`docs-brand-chip-${encoded}`}
                className={cn(
                  "rounded-chip border px-2.5 py-1 font-mono text-[0.625rem] uppercase tracking-[0.08em]",
                  activeBrand === b.name
                    ? "border-os-ink-3 bg-os-wash text-os-ink"
                    : "border-os-border text-os-ink-3 hover:bg-os-wash",
                )}
              >
                {b.name} · {b.count}
              </button>
            );
          })}
        </div>
      )}

      {!query && lanes && Object.keys(lanes).length > 0 && (
        <div
          className="flex flex-wrap gap-1.5 border-b border-os-border px-4 py-2.5"
          data-testid="docs-lanes"
        >
          <button
            type="button"
            onClick={() => onLane("")}
            data-testid="docs-lane-all"
            className={cn(
              "rounded-chip border px-2.5 py-1 font-mono text-[0.625rem] uppercase tracking-[0.08em]",
              !activeLane
                ? "border-os-ink-3 bg-os-wash text-os-ink"
                : "border-os-border text-os-ink-3 hover:bg-os-wash",
            )}
          >
            All
          </button>
          {sortLanes(lanes).map((lane) => (
            <button
              type="button"
              key={lane}
              onClick={() => onLane(activeLane === lane ? "" : lane)}
              data-testid={`docs-lane-${lane}`}
              className={cn(
                "rounded-chip border px-2.5 py-1 font-mono text-[0.625rem] uppercase tracking-[0.08em]",
                activeLane === lane
                  ? "border-os-ink-3 bg-os-wash text-os-ink"
                  : "border-os-border text-os-ink-3 hover:bg-os-wash",
              )}
            >
              {laneLabel(lane)} · {lanes[lane]}
            </button>
          ))}
        </div>
      )}

      <div
        className="min-h-0 flex-1 overflow-y-auto"
        data-testid="docs-list-scroll"
      >
        {error && (
          <p
            className="px-4 py-6 text-[0.875rem] text-os-red"
            data-testid="docs-error"
          >
            {error}
          </p>
        )}
        {!error && loading && items.length === 0 && (
          <p
            className="px-4 py-6 text-[0.875rem] text-os-ink-3"
            data-testid="docs-loading"
          >
            Loading documents…
          </p>
        )}
        {!error && loading && items.length > 0 && (
          <p
            className="px-4 py-2 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-4"
            data-testid="docs-updating"
          >
            updating…
          </p>
        )}
        {!error && !loading && items.length === 0 && (
          <p
            className={cn(
              "py-6 text-[0.875rem] text-os-ink-3",
              query ? "px-4" : "w-full px-4 text-center",
            )}
            data-testid="docs-empty"
          >
            {query
              ? "No document matches that search yet."
              : "No documents have synced yet — Kit's next pass brings them in."}
          </p>
        )}
        {contextual && siblings.length > 0 && (
          <div data-testid="docs-siblings-section">
            <Label className="block px-4 pb-1 pt-4">Same folder</Label>
            <div>
              {siblings.map((it) => (
                <DocRow
                  key={it.id}
                  it={it}
                  onOpen={onOpen}
                  testid={`docs-sibling-${it.id}`}
                />
              ))}
            </div>
          </div>
        )}
        {contextual && recent.length > 0 && (
          <div data-testid="docs-recent-section">
            <Label className="block px-4 pb-1 pt-4">Recently viewed</Label>
            <div>
              {recent.map((it) => (
                <DocRow
                  key={it.id}
                  it={it}
                  onOpen={onOpen}
                  testid={`docs-recent-${it.id}`}
                />
              ))}
            </div>
          </div>
        )}
        {items.map((it) => {
          // While reading WITH a search active, the list itself is the doc's
          // context — skip the open doc's own row so the read target never
          // re-opens itself.
          if (openId && query && it.id === openId) return null;
          const laneItems = grouped.get(it.lane || "other") || [];
          const first = laneItems[0] === it;
          return (
            <div key={it.id}>
              {first && (
                <Label className="block px-4 pb-1 pt-4">
                  {laneLabel(it.lane || "other")}
                </Label>
              )}
              <DocRow
                it={it}
                onOpen={onOpen}
                testid={`docs-item-${it.id}`}
                reading={it.id === openId}
              />
            </div>
          );
        })}
        {items.length > 0 && (
          <p className="px-4 py-4 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-4">
            {items.length} shown · {total} in the library
          </p>
        )}
      </div>
    </div>
  );
};

// ── the backlinks panel (P3.1) ─────────────────────────────────────────────
// "What links here" — the documents that cite the doc being read, resolved
// from the link index the doc-sync lane maintains (DOC-BACKLINKS-CONTRACT.md).
//
// Three render states, one fetch: the rows (live count beside the title),
// the empty state ("No backlinks yet." — real UI, not silence), and a
// one-line amber note when the fetch failed with anything other than a 404.
// A 404 means the doc itself is unknown → no panel at all. The fetch fires
// once per doc load (ride loadDoc's lifecycle, never polled).
//
// The panel lives UNDER the metadata bar: not a tab, no navigation — a
// citation ledger that rides the read view. Rows are REAL anchors
// (react-router <Link>, href preserved) so middle-click/cmd-click work; the
// click routes in-tab to the other doc's reader route — a reader-to-reader
// hop, never a new tab.
const BacklinksPanel = ({ docId, reloadKey }) => {
  const [backlinks, setBacklinks] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!docId) return;
    let live = true;
    setBacklinks(null);
    setFailed(false);
    fetchDocBacklinks(docId)
      .then((body) => {
        if (live) setBacklinks(Array.isArray(body?.backlinks) ? body.backlinks : []);
      })
      .catch((e) => {
        if (!live) return;
        // A 404 for the doc itself → the panel drops out silently. Anything
        // else is one amber line, in the screen's own words, never a code.
        if (e?.status !== 404) setFailed(true);
      });
    return () => {
      live = false;
    };
  }, [docId, reloadKey]);

  if (failed) {
    return (
      <div
        className="mx-6 mb-4 border-t border-os-border pt-3"
        data-testid="docs-backlinks"
      >
        <p
          className="text-[0.8125rem] text-os-amber"
          data-testid="docs-backlinks-error"
        >
          The list of linking documents isn't answering right now.
        </p>
      </div>
    );
  }
  const rows = backlinks || [];
  return (
    <div
      className="mx-6 mb-4 border-t border-os-border pt-3"
      data-testid="docs-backlinks"
    >
      <Label>What links here ({rows.length})</Label>
      {rows.length === 0 ? (
        <p
          className="mt-2 text-[0.8125rem] text-os-ink-3"
          data-testid="docs-backlinks-empty"
        >
          No backlinks yet.
        </p>
        ) : (
          <ul className="mt-2 flex flex-col" data-testid="docs-backlinks-list">
            {rows.map((row) => {
              const href = `/docs/${encodeURIComponent(row.docId)}`;
              return (
                <li key={`${row.docId}-${row.path}`}>
                  <ReactRouterLink
                    to={href}
                    data-testid={`docs-backlink-${row.docId}`}
                    className="block rounded-control border border-transparent px-3 py-2 hover:border-os-border hover:bg-os-wash"
                  >
                    <span className="flex min-w-0 items-baseline gap-2">
                      <span className="min-w-0 flex-1 truncate text-[0.8125rem] text-os-ink">
                        {row.title || row.path}
                      </span>
                      {row.lane && (
                        <span className="shrink-0 rounded-chip border border-os-border px-1.5 py-0.5 font-mono text-[0.5625rem] uppercase tracking-[0.08em] text-os-ink-4">
                          {row.lane}
                        </span>
                      )}
                    </span>
                    {row.snippet && (
                      <span className="mt-0.5 block line-clamp-2 text-[0.75rem] leading-[1.4] text-os-ink-2">
                        {row.snippet}
                      </span>
                    )}
                  </ReactRouterLink>
                </li>
              );
            })}
          </ul>
        )}
    </div>
  );
};

// ── P3.4 md quote anchoring ───────────────────────────────────────────────
// Find a quote in the current content, the way the contract re-finds it at
// render: an exact indexOf guided by quote_start first, then a
// whitespace/markup-flexible pass (a selection saved from one line wraps
// across a line break; a saved quote may carry emphasis markers). Returns
// {at, len} or null.
const findQuote = (content, quote, start) => {
  if (!content || !quote) return null;
  const from = Math.max(0, Number(start) || 0);
  let at = content.indexOf(quote, from);
  if (at === -1 && from > 0) at = content.indexOf(quote);
  if (at !== -1) return { at, len: quote.length };
  const words = String(quote)
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  if (words.length === 0) return null;
  const rx = new RegExp(words.join("[^\\w\\r\\n]*"), "m");
  const m = rx.exec(content);
  if (!m) return null;
  return { at: m.index, len: m[0].length };
};

// A remark plugin that wraps saved quotes in highlight spans. The tree is
// the final mdast (runs after gfm and the doc-link pass), so every text node
// carries source position offsets. A quote range may cross sibling text
// nodes (emphasis boundaries); each covered node wraps its own slice with
// the same data-annot-id, so the click still opens the one thread.
function remarkHighlights(options = {}) {
  const highlights = options.highlights || [];
  const ranges = highlights
    .map((h) => ({ id: String(h.row.id), at: h.at, len: h.len || h.row.quote.length }))
    .sort((a, b) => a.at - b.at);

  const wrap = (node) => {
    const pos = node.position;
    if (!pos || typeof pos.start.offset !== "number") return null;
    const s = pos.start.offset;
    const e = pos.end.offset;
    const out = [];
    let cursor = s;
    const value = node.value || "";
    for (const r of ranges) {
      if (r.at >= e || r.at + r.len <= s) continue;
      const cutAt = Math.max(r.at, s);
      const cutEnd = Math.min(r.at + r.len, e);
      if (cutEnd <= cursor) continue;
      if (cutAt > cursor) out.push({ type: "text", value: value.slice(cursor - s, cutAt - s) });
      out.push({
        type: "emphasis",
        data: {
          hName: "span",
          hProperties: {
            "data-testid": "docs-md-highlight",
            "data-annot-id": r.id,
          },
        },
        children: [{ type: "text", value: value.slice(cutAt - s, cutEnd - s) }],
      });
      cursor = cutEnd;
    }
    if (out.length === 0) return null;
    if (cursor < e) out.push({ type: "text", value: value.slice(cursor - s) });
    return out;
  };

  return (tree) => {
    const walk = (node) => {
      if (!node || !Array.isArray(node.children)) return;
      const out = [];
      for (const child of node.children) {
        if (child.type === "text") {
          const pieces = wrap(child);
          if (pieces) {
            out.push(...pieces);
            continue;
          }
        }
        walk(child);
        out.push(child);
      }
      node.children = out;
    };
    walk(tree);
  };
}

// The md read view with highlights: one component so the plugin array is
// rebuilt only when its inputs change. Clicking any highlight opens that
// annotation's thread — the click is delegated on the WRAPPER (the reader's
// article element in DocsPage), because react-markdown v10 renders its own
// root and drops unknown props like onClick.
const MdAnnotated = ({
  content,
  highlights,
  resolve,
  basePath,
  components,
}) => {
  const plugins = useMemo(
    () => [
      [remarkDocLinks, { resolve, basePath }],
      [remarkHighlights, { highlights }],
    ],
    [resolve, basePath, highlights],
  );
  return (
    <ReactMarkdown
      remarkPlugins={plugins}
      rehypePlugins={[rehypeSlug]}
      urlTransform={(url) =>
        url && url.startsWith("doc:") ? url : defaultUrlTransform(url)
      }
      components={components}
    >
      {content}
    </ReactMarkdown>
  );
};

// ── the reader ────────────────────────────────────────────────────────────

const DocReader = ({ docId, onBack, onOpenDoc }) => {
  const [doc, setDoc] = useState(null);
  const [history, setHistory] = useState(null);
  const [tab, setTab] = useState("read");
  const [compare, setCompare] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState({});

  // ── the edit (P2) ── dual modes over ONE doc state. The reader stays the
  // source of truth; the editor borrows its text. Entering edit mode seeds
  // the draft from what is on the page; leaving it never touches `doc`.
  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [draftBusy, setDraftBusy] = useState(false);
  // The edit in flight: {edit_id, state, detail} — from the POST's own
  // response first, then kept fresh by the poll below.
  const [edit, setEdit] = useState(null);
  const [ending, setEnding] = useState(false);
  const [toast, setToast] = useState("");
  const editRef = useRef(null);
  editRef.current = edit;
  const docRef = useRef(null);
  docRef.current = doc;

  const loadDoc = useCallback(
    (keepDraft) => {
      let live = true;
      setLoading(true);
      setError("");
      setCompare("");
      setTab("read");
      Promise.all([fetchDoc(docId), fetchDocIndex()])
        .then(([d, idx]) => {
          if (!live) return;
          setDoc(d);
          setIndex(idx.index || {});
          // The draft may survive a reload (conflict keeps it); it is
          // reseeded from the fresh text only when it is not being kept.
          if (!keepDraft) setDraft(d.content || "");
          setLoading(false);
        })
        .catch((e) => {
          if (!live) return;
          setError(e.message || "That document didn't load.");
          setLoading(false);
        });
      return () => {
        live = false;
      };
    },
    [docId],
  );

  useEffect(() => {
    setEditOpen(false);
    setEdit(null);
    setDraft("");
    loadDoc(false);
  }, [docId, loadDoc]);

  // The history is fetched when the tab is opened, and again when a version
  // is picked (that is when the diff against the current text arrives).
  useEffect(() => {
    if (tab !== "history" || !docId) return;
    let live = true;
    fetchDocHistory(docId, compare)
      .then((h) => {
        if (live) setHistory(h);
      })
      .catch(() => {
        if (live) setHistory({ versions: [] });
      });
    return () => {
      live = false;
    };
  }, [tab, docId, compare]);

  // The settle after an applied edit: reload the document (its content AND
  // content_sha8 move) AND its history (a new version was recorded), then
  // leave edit mode.
  const reloadAfterApplied = useCallback(() => {
    if (!docId) return;
    fetchDoc(docId)
      .then((d) => {
        if (docRef.current && docRef.current.id !== d.id) return;
        setDoc(d);
        setDraft(d.content || "");
      })
      .catch(() => {});
    fetchDocHistory(docId, "")
      .then((h) => setHistory(h))
      .catch(() => {});
  }, [docId]);

  const toastTimer = useRef(null);
  const sayToast = useCallback((word) => {
    setToast(word);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(""), 4200);
  }, []);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  // The poll: while an edit is queued or applying, GET /edits every 5s.
  // One fetch lands the settled answer — no wait for the next tick. The
  // poll carries the docId it started with and ignores rows that answer a
  // different document (the reader may have navigated between calls).
  const pollEditRef = useRef(null);
  useEffect(() => () => clearInterval(pollEditRef.current), []);
  const editState = edit?.state;
  const editInFlight = editState === "queued" || editState === "applying";
  useEffect(() => {
    if (!editInFlight) return;
    let stopped = false;
    const tick = async () => {
      try {
        const body = await fetchDocEdits(docId);
        if (stopped) return;
        const row = activeEditOf(body);
        if (!row) return;
        if (row.doc_id && docId && row.doc_id !== docId) return;
        setEdit({
          state: row.state,
          detail: row.detail || "",
          edit_id: row.edit_id || row.id,
        });
        if (row.state === "applied") {
          reloadAfterApplied();
          setEditOpen(false);
          sayToast("Edit applied");
        } else if (row.state === "cancelled") {
          sayToast("Edit cancelled");
        }
      } catch {
        // A single unanswered poll says nothing — the next tick asks again.
      }
    };
    tick();
    const t = setInterval(tick, 5000);
    pollEditRef.current = t;
    return () => {
      stopped = true;
      clearInterval(t);
      pollEditRef.current = null;
    };
  }, [editInFlight, docId, reloadAfterApplied, sayToast]);

  // The chip is the IN-FLIGHT indicator: a queued/applying edit shows the
  // chip; once a terminal word lands (applied, conflict, failed…), the
  // story moves to the wider banner and the chip drops out of the header.
  const EDIT_IN_FLIGHT = ["queued", "applying"];

  // Save: the REQUEST. content + base_sha8 — the sha8 of the EXACT text
  // this editor opened (GET /api/docs/(id)'s content_sha8), not a sha of
  // the draft. The POST's own response primes the chip; the poll confirms.
  const saveEdit = useCallback(async () => {
    if (!doc) return;
    // An in-flight edit is the one thing a Save must not double-post. A
    // settled one (conflict / failed / cancelled) is a fine moment to ask
    // again — "Save again" rides this same callback.
    if (edit && (edit.state === "queued" || edit.state === "applying")) {
      return;
    }
    setDraftBusy(true);
    try {
      const body = await postDocEdit(doc.id, {
        content: draft,
        base_sha8: doc.content_sha8,
      });
      setEdit({
        state: body.state || "queued",
        detail: body.detail || "",
        edit_id: body.edit_id || body.id,
      });
      setEditOpen(false);
    } catch (e) {
      sayToast(e.message || "That didn't go through — try again or tell Kit.");
    } finally {
      setDraftBusy(false);
      setEditOpen(false);
    }
  }, [doc, draft, edit, sayToast]);

  // Cancel affordance: POST /edits/(id)/cancel. Stops the poll and clears
  // the chip; the draft is untouched. A 409 while the edit is applying (the
  // Mac is mid-write) or a rejection after the edit already settled is not
  // an error raised here — the poll's answer settles the truth.
  const cancelEdit = useCallback(async () => {
    const current = editRef.current;
    const id = current?.edit_id;
    setEnding(true);
    try {
      if (id) await postDocEditCancel(id);
    } catch {
      // 409 (already applying) or already-settled — fine either way.
    }
    setEnding(false);
    setEdit(null);
    sayToast("Edit cancelled");
  }, [sayToast]);

  // The reader's Cancel affordance with no chip yet (a queued edit from
  // another tab is on this doc): fetch the row, post the cancel, leave the
  // poll — the next tick reports the row cancelled.
  const cancelInFlight = useCallback(async () => {
    if (edit) return cancelEdit();
    setEnding(true);
    try {
      const body = await fetchDocEdits(docId);
      const row = activeEditOf(body);
      if (row && (row.state === "queued" || row.state === "applying")) {
        const id = row.edit_id || row.id;
        try {
          await postDocEditCancel(id);
          sayToast("Edit cancelled");
        } catch {
          // 409 while applying, or already settled — fine either way.
        }
      }
    } catch {
      // The edits list not answering is not a thing to raise here.
    }
    setEnding(false);
  }, [cancelEdit, docId, edit, sayToast]);

  // ── P3.4 annotations (md lane) ───────────────────────────────────────────
  // The md reader's half of DOC-ANNOT-CONTRACT.md: text selection in the read
  // tab shows a Note affordance; saving creates a quote-anchored annotation;
  // quotes re-found in the current text render as highlights through a
  // remark plugin; quotes that no longer match serve `stale:true` and list
  // in a "Moved notes" box above the content. A 401 (session not allowed to
  // see annotations) hides every affordance — the reader never crashes over
  // the annotations store.
  const [annotRows, setAnnotRows] = useState([]);
  const [annotsBlocked, setAnnotsBlocked] = useState(false);
  const [selDraft, setSelDraft] = useState(null); // {quote, quote_start, quote_context}
  const [noteDraft, setNoteDraft] = useState("");
  const [noteBusy, setNoteBusy] = useState(false);
  const [noteErr, setNoteErr] = useState("");
  const [openThreadId, setOpenThreadId] = useState(null);

  // The nearest heading ABOVE a char offset — the contract's quote_context.
  const contextHeading = (text, at) => {
    const upTo = text.slice(0, at);
    let best = null;
    for (const m of upTo.matchAll(/^#{1,6}\s+(.+)$/gm)) {
      best = m[1].trim();
    }
    return best || "";
  };

  // Quote anchoring: the row must re-find its quote in CURRENT content.
  // findQuote is the whole ladder: an exact indexOf guided by quote_start
  // (forward from it, then anywhere), then the whitespace/markup-flexible
  // pass a plain indexOf lacks. A quote_context heading disambiguates when
  // the re-found match sits under a different heading than the one the
  // quote was saved under.
  const locators = useMemo(() => {
    const content = doc?.kind === "md" ? doc?.content || "" : "";
    if (!content) return [];
    const list = (annotRows || []).filter((r) => !r.parent_id && r.quote);
    return list.map((r) => {
      const found = findQuote(content, r.quote, r.quote_start);
      let at = found ? found.at : -1;
      if (at !== -1 && r.quote_context) {
        const ctx = contextHeading(content, at);
        if (ctx && ctx !== r.quote_context) at = -1;
      }
      return { row: r, at };
    });
  }, [doc, annotRows]);

  const liveHighlights = locators.filter((l) => l.at >= 0);
  const staleRows = (annotRows || []).filter(
    (r) => r.stale || !locators.some((l) => l.row.id === r.id && l.at >= 0),
  );

  // Fetch once per doc; 401 → affordances hidden, reader keeps working.
  useEffect(() => {
    setAnnotRows([]);
    setAnnotsBlocked(false);
    setSelDraft(null);
    setNoteDraft("");
    setOpenThreadId(null);
    if (!docId) return undefined;
    let live = true;
    fetchDocAnnotations(docId)
      .then((body) => {
        if (!live) return;
        const rows = Array.isArray(body?.annotations)
          ? body.annotations
          : Array.isArray(body)
            ? body
            : [];
        setAnnotRows(rows);
      })
      .catch((e) => {
        if (!live) return;
        if (e?.status === 401) setAnnotsBlocked(true);
        setAnnotRows([]);
      });
    return () => {
      live = false;
    };
  }, [docId]);

  const refetchAnnots = useCallback((id) => {
    fetchDocAnnotations(id)
      .then((body) => {
        const rows = Array.isArray(body?.annotations)
          ? body.annotations
          : Array.isArray(body)
            ? body
            : [];
        setAnnotRows(rows);
      })
      .catch(() => {
        /* silent: keep what we had */
      });
  }, []);

  // Selection in one block → the Note affordance. A selection spanning
  // multiple blocks is not anchored (contract: document-level notes are a
  // non-goal in P1) — no affordance.
  const onReadSelection = useCallback(() => {
    if (annotsBlocked) return;
    const sel = window.getSelection && window.getSelection();
    const text = sel ? String(sel.toString()) : "";
    if (!text.trim()) {
      setSelDraft(null);
      return;
    }
    const anchorNode = sel.anchorNode;
    const focusNode = sel.focusNode;
    const readEl = document.querySelector('[data-testid="docs-content"]');
    if (!readEl || !anchorNode || !focusNode) {
      setSelDraft(null);
      return;
    }
    if (!readEl.contains(anchorNode) || !readEl.contains(focusNode)) {
      setSelDraft(null);
      return;
    }
    const block = (n) =>
      n && n.parentElement && n.parentElement.closest("p, li, h1, h2, h3, h4, h5, h6, blockquote, td, th");
    const ba = block(anchorNode);
    const bf = block(focusNode);
    if (!ba || !bf || ba !== bf) {
      setSelDraft(null);
      return;
    }
    const quote = text.replace(/\s+/g, " ").trim();
    if (!quote) {
      setSelDraft(null);
      return;
    }
    const content = docRef.current?.content || "";
    // findQuote, not bare indexOf: a selection saved across a line wrap or
    // an emphasis marker still yields a usable quote_start (the anchor the
    // contract re-finds from).
    const found = findQuote(content, quote, 0);
    const at = found ? found.at : -1;
    setSelDraft({
      quote,
      quote_start: at >= 0 ? at : 0,
      quote_context: contextHeading(content, at >= 0 ? at : 0),
    });
  }, [annotsBlocked]);

  // Save the note: POST {body, quote, quote_start, quote_context}; the
  // response row joins the list and the highlight appears immediately.
  const saveNote = useCallback(async () => {
    const body = noteDraft.trim();
    if (!body || !selDraft || !docId) return;
    setNoteBusy(true);
    setNoteErr("");
    try {
      await createDocAnnotation(docId, {
        body,
        quote: selDraft.quote,
        quote_start: selDraft.quote_start,
        quote_context: selDraft.quote_context,
      });
      setSelDraft(null);
      setNoteDraft("");
      refetchAnnots(docId);
    } catch (e) {
      setNoteErr(e.message || "That didn't go through — try again or tell Kit.");
    } finally {
      setNoteBusy(false);
    }
  }, [docId, noteDraft, selDraft, refetchAnnots]);

  // Same doors the pdf boxes use — the thread panel is shared.
  const toggleNoteResolve = useCallback(
    async (thread) => {
      const nextState = thread.state === "resolved" ? "open" : "resolved";
      try {
        const updated = await updateDocAnnotation(thread.id, { state: nextState });
        setAnnotRows((prev) =>
          prev.map((a) =>
            a.id === thread.id
              ? {
                  ...a,
                  ...(updated && typeof updated === "object" ? updated : {}),
                  id: a.id,
                }
              : a,
          ),
        );
      } catch (e) {
        setNoteErr(e.message || "That didn't go through — try again or tell Kit.");
      }
    },
    [],
  );

  const sendNoteReply = useCallback(
    async (thread, body) => {
      const reply = await replyDocAnnotation(thread.id, body);
      setAnnotRows((prev) =>
        prev.map((a) => {
          if (a.id !== thread.id) return a;
          const child =
            reply && typeof reply === "object" && reply.parent_id
              ? reply
              : {
                  id: `temp-${Date.now()}`,
                  parent_id: thread.id,
                  body,
                  author: "jeff",
                  created_at: new Date().toISOString(),
                };
          return { ...a, replies: [...repliesOf(a), child] };
        }),
      );
    },
    [],
  );

  const openThreadRow =
    (annotRows || []).find((a) => a.id === openThreadId) || null;

  const resolve = useCallback(
    (ref) => {
      if (!ref) return null;
      if (index[ref]) return index[ref];
      // A wikilink may name the file without its extension.
      const withExt = ref.endsWith(".md") ? ref : `${ref}.md`;
      return index[withExt] || null;
    },
    [index],
  );


  const openRef = useCallback(
    (id) => {
      if (id) onOpenDoc(id);
    },
    [onOpenDoc],
  );

  const components = useMemo(
    () => ({
      // A link to another document in the library: the reader opens it IN
      // the app. A link to the outside world opens in a new tab, the way
      // the web expects. A link to a document the index does not know stays
      // plain text — a dead link must not pretend to work.
      a: ({ href, children }) => {
        const target = String(href || "");
        const docId = docIdFromUrl(target);
        if (docId) {
          return (
            <button
              type="button"
              onClick={() => openRef(docId)}
              data-testid="docs-doclink"
              data-doc-id={docId}
              className="inline border-b border-os-bronze text-os-ink hover:text-os-amber"
            >
              {children}
            </button>
          );
        }
        if (target.startsWith("#")) {
          return (
            <a
              href={target}
              className="text-os-ink underline decoration-os-bronze"
            >
              {children}
            </a>
          );
        }
        return (
          <a
            href={target}
            target="_blank"
            rel="noreferrer"
            className="text-os-ink underline decoration-os-bronze"
          >
            {children}
          </a>
        );
      },
    }),
    [openRef],
  );

  if (loading) {
    return (
      <div className="px-6 py-8" data-testid="docs-reader-loading">
        <p className="text-[0.875rem] text-os-ink-3">Opening the document…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="px-6 py-8" data-testid="docs-reader-error">
        <p className="text-[0.875rem] text-os-red">{error}</p>
        <button
          type="button"
          onClick={onBack}
          className="mt-3 text-[0.875rem] text-os-ink-2 underline"
        >
          Back to the list
        </button>
      </div>
    );
  }
  if (!doc) return null;

  // History clarity (Jeff 11-04): rows are git commits that touched this
  // file. Two families exist — edits made through this reader (those
  // messages carry the "[doc-edit via AgenticOS]" prefix) and everything
  // the workspace sync lane committed. A commit message often names OTHER
  // documents (bookkeeping commits name the job, not the file), so each
  // row is labeled by family and headlined by its own diff.
  const EDIT_PREFIX = "[doc-edit via AgenticOS]";
  const splitEdit = (v) => {
    const m = v.message || "";
    if (m.startsWith(EDIT_PREFIX)) {
      return {
        kind: "edit",
        message: m.slice(EDIT_PREFIX.length).trim() || "Reader edit",
      };
    }
    return { kind: "sync", message: m || "No message" };
  };
  const diffStats = (d) => {
    if (!d) return null; // empty diff: nothing to say, render nothing
    let a = 0;
    let r = 0;
    for (const line of String(d).split("\n")) {
      if (line.startsWith("+++") || line.startsWith("---")) continue;
      if (line.startsWith("+")) a += 1;
      else if (line.startsWith("-")) r += 1;
    }
    return {
      a,
      r,
      text:
        a === 0 && r === 0 ? "no textual changes" : `${a} added · ${r} removed`,
    };
  };
  const versions = (history?.versions || doc.versions || []).map((v) => {
    const { kind, message } = splitEdit(v);
    return { ...v, editKind: kind, cleanMessage: message, stats: diffStats(v.diff) };
  });
  const diff = history?.diff || "";

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="docs-reader">
      <header className="border-b border-os-border px-6 py-4">
        <button
          type="button"
          onClick={onBack}
          data-testid="docs-back"
          className="mb-2 inline-flex items-center gap-1.5 text-[0.75rem] text-os-ink-3 hover:text-os-ink lg:hidden"
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.75} />
          All documents
        </button>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="truncate text-[1.25rem] font-semibold tracking-[-0.01em]">
              {doc.title}
            </h1>
            <p className="mt-1 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-4">
              {/* P2: the path in the metadata line opens the same doc in a new tab
                  (openInDocs resolves via the doc index; falls back to search). */}
              <button
                type="button"
                data-testid="docs-meta-path"
                onClick={() => openInDocs(doc.path)}
                className="cursor-pointer uppercase hover:text-os-ink-2 hover:underline"
              >
                {doc.path}
              </button>
              {" · "}
              {bytes(doc.size)}
              {doc.updatedAt ? ` · ${stampText(doc.updatedAt)}` : ""}
            </p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-2">
            {doc.kind === "md" && !editOpen && (
              <button
                type="button"
                onClick={() => {
                  setDraft(doc.content || "");
                  setEditOpen(true);
                }}
                disabled={Boolean(
                  edit &&
                  (edit.state === "queued" || edit.state === "applying"),
                )}
                data-testid="docs-edit"
                className="inline-flex items-center gap-1.5 rounded-control border border-os-border bg-white px-3 py-1.5 text-[0.8125rem] text-os-ink hover:bg-os-wash disabled:opacity-50"
              >
                <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} />
                Edit
              </button>
            )}
            {edit && EDIT_IN_FLIGHT.includes(edit.state) && (
              <div className="inline-flex items-center gap-2">
                <div
                  data-testid="docs-edit-chip"
                  data-edit-state={edit.state}
                  className="inline-flex items-center gap-2 rounded-chip border border-os-border px-2.5 py-1 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-3"
                >
                  <span
                    aria-hidden="true"
                    className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-current"
                  />
                  edit · {EDIT_STATE_WORD[edit.state] || edit.state}
                </div>
                <button
                  type="button"
                  onClick={cancelEdit}
                  disabled={ending}
                  data-testid="docs-edit-cancel"
                  className="text-[0.75rem] text-os-ink-3 underline hover:text-os-ink"
                >
                  Cancel
                </button>
              </div>
            )}
            {edit?.detail && !["failed", "conflict"].includes(edit.state) && (
              <p
                className="max-w-[28rem] text-right font-mono text-[0.625rem] text-os-ink-3"
                data-testid="docs-edit-detail"
              >
                {edit.detail}
              </p>
            )}
          </div>
        </div>

        <div className="mt-4 flex gap-1" role="tablist">
          {!editOpen &&
            [
              ["read", "Read"],
              [
                "history",
                `History${doc.versionCount ? ` (${doc.versionCount})` : ""}`,
              ],
              ...(doc.hasPdf ? [["pdf", "PDF"]] : []),
            ].map(([key, label]) => (
              <button
                type="button"
                key={key}
                role="tab"
                aria-selected={tab === key}
                onClick={() => setTab(key)}
                data-testid={`docs-tab-${key}`}
                className={cn(
                  "rounded-control px-3 py-1.5 text-[0.8125rem]",
                  tab === key
                    ? "bg-os-wash font-medium text-os-ink"
                    : "text-os-ink-3 hover:bg-os-wash hover:text-os-ink",
                )}
              >
                {label}
              </button>
            ))}
          {editOpen && (
            <>
              <span
                role="tab"
                aria-selected="true"
                data-testid="docs-tab-edit"
                className="rounded-control bg-os-wash px-3 py-1.5 text-[0.8125rem] font-medium text-os-ink"
              >
                Edit
              </span>
              {!EDIT_IN_FLIGHT.includes(edit?.state) && (
                <button
                  type="button"
                  onClick={() => setEditOpen(false)}
                  data-testid="docs-edit-cancel"
                  className="rounded-control px-3 py-1.5 text-[0.8125rem] text-os-ink-3 hover:bg-os-wash hover:text-os-ink"
                >
                  Cancel
                </button>
              )}
            </>
          )}
        </div>
        {/* P3.1 backlinks: "What links here" rides the metadata bar directly —
            under it, above the tabs, for every loaded doc (md and pdf alike;
            the 404 doc never reaches here — its error view replaces the reader). */}
        {(doc.kind === "md" || doc.kind === "pdf") && (
          <BacklinksPanel docId={docId} reloadKey={doc.versionCount || 0} />
        )}
      </header>

      <div className="min-h-0 flex-1">
        {/* The toast — the calm confirmation, top of the body. */}
        {toast && (
          <p
            role="status"
            data-testid="docs-toast"
            className="border-b border-os-border px-6 py-2.5 text-[0.8125rem] text-os-green"
          >
            {toast}
          </p>
        )}
        {/* The failed banner shows in either mode. */}
        {edit?.state === "failed" && (
          <div
            className="border-b border-os-red px-6 py-3"
            data-testid="docs-edit-failed"
          >
            <p
              className="text-[0.8125rem] text-os-red"
              data-testid="docs-edit-failed-text"
            >
              The edit didn't land{edit.detail ? ` — ${edit.detail}` : "."}
            </p>
          </div>
        )}
        {/* The cancelled banner shows in either mode. */}
        {edit?.state === "cancelled" && (
          <div
            className="border-b border-os-border px-6 py-3"
            data-testid="docs-edit-cancelled-banner"
          >
            <p className="text-[0.8125rem] text-os-ink-3">
              The edit was cancelled — nothing was written.
            </p>
          </div>
        )}
        {/* The conflict banner — amber, in either mode. */}
        {edit?.state === "conflict" && (
          <div
            className="border-b border-os-amber px-6 py-3"
            data-testid="docs-conflict"
          >
            <p className="text-[0.8125rem] text-os-amber">
              The file changed on the Mac since this edit started. Your edit is
              preserved.
            </p>
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setEdit(null);
                  setEditOpen(false);
                  loadDoc(true);
                }}
                data-testid="docs-conflict-reload"
                className="rounded-control border border-os-amber px-2.5 py-1 text-[0.8125rem] text-os-amber hover:bg-os-wash"
              >
                Reload
              </button>
              <button
                type="button"
                onClick={saveEdit}
                className="rounded-control border border-os-border bg-white px-2.5 py-1 text-[0.8125rem] text-os-ink hover:bg-os-wash"
              >
                Save again
              </button>
            </div>
          </div>
        )}
        {editOpen ? (
          <>
            <div className="flex h-full min-h-0 flex-col overflow-y-auto px-6 py-4">
              <CodeMirror
                value={draft}
                onChange={setDraft}
                extensions={[markdownLanguage()]}
                basicSetup={{
                  lineNumbers: true,
                  foldGutter: false,
                  highlightActiveLine: false,
                  autocompletion: false,
                }}
                height="60vh"
                data-testid="docs-editor"
                data-prosemirror="false"
                className="docs-editor"
              />
              <div className="flex items-center justify-end gap-2 py-3">
                <button
                  type="button"
                  onClick={() => setEditOpen(false)}
                  data-testid="docs-editor-cancel"
                  className="rounded-control border border-os-border bg-white px-3 py-1.5 text-[0.8125rem] text-os-ink hover:bg-os-wash"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveEdit}
                  disabled={draftBusy}
                  data-testid="docs-editor-save"
                  className="rounded-control border border-os-ink bg-os-wash px-3 py-1.5 text-[0.8125rem] font-medium text-os-ink hover:bg-os-border disabled:opacity-50"
                >
                  Save
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            {tab === "read" && (
              <div className="flex min-h-0 flex-1">
                <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 py-6">
                  {/* P3.4: quotes the current text no longer contains serve
                      stale:true — the "Moved notes" box keeps them visible
                      above the content (never silent loss per the contract). */}
                  {doc.kind === "md" && !annotsBlocked && staleRows.length > 0 && (
                    <div
                      data-testid="docs-annot-stale"
                      className="mb-4 rounded-card border border-os-amber bg-os-wash px-3 py-2.5"
                    >
                      <p className="text-[0.8125rem] font-medium text-os-amber">
                        Moved notes ({staleRows.length})
                      </p>
                      <ul className="mt-1.5 flex flex-col gap-1.5">
                        {staleRows.map((r) => (
                          <li key={r.id}>
                            <button
                              type="button"
                              data-testid="docs-annot-stale-row"
                              onClick={() => setOpenThreadId(r.id)}
                              className="block w-full rounded-control border border-transparent px-2 py-1.5 text-left hover:border-os-border hover:bg-white"
                            >
                              <span className="block text-[0.8125rem] text-os-ink">
                                {r.body}
                              </span>
                              {r.quote && (
                                <span className="mt-0.5 block truncate text-[0.7rem] italic text-os-ink-3">
                                  “{r.quote}”
                                </span>
                              )}
                              {r.quote_context && (
                                <span className="mt-0.5 block font-mono text-[0.5625rem] uppercase tracking-[0.08em] text-os-ink-4">
                                  was under “{r.quote_context}”
                                </span>
                              )}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <article
                    className="doc-surface min-h-0 flex-1"
                    data-testid="docs-content"
                    onMouseUp={doc.kind === "md" ? () => onReadSelection() : undefined}
                    onTouchEnd={doc.kind === "md" ? () => onReadSelection() : undefined}
                    onClick={
                      doc.kind === "md" && !annotsBlocked
                        ? (e) => {
                            const box =
                              e.target && e.target.closest
                                ? e.target.closest("[data-annot-id]")
                                : null;
                            if (box)
                              setOpenThreadId(box.getAttribute("data-annot-id"));
                          }
                        : undefined
                    }
                  >
                    <MdAnnotated
                      content={doc.content || ""}
                      highlights={annotsBlocked ? [] : liveHighlights}
                      resolve={resolve}
                      basePath={doc.path}
                      docId={docId}
                      components={components}
                    />
                  </article>
                </div>
                {/* The Note affordance: selection in one block floats it in the
                    pane; the composer rides the thread rail's width for layout
                    stability while it is open. */}
                {!annotsBlocked && selDraft && (
                  <div
                    data-testid="docs-md-note-affordance"
                    className="w-[17rem] shrink-0 border-l border-os-border bg-white px-3 py-3"
                  >
                    <p className="text-[0.8125rem] font-medium text-os-ink">
                      Note on this selection
                    </p>
                    <p className="mt-1 border-l-2 border-os-bronze pl-2 text-[0.75rem] italic leading-[1.45] text-os-ink-2">
                      “{selDraft.quote}”
                    </p>
                    <textarea
                      autoFocus
                      value={noteDraft}
                      onChange={(e) => setNoteDraft(e.target.value)}
                      placeholder="What should Kit or Jeff know about this?"
                      data-testid="docs-md-note-text"
                      rows={4}
                      className="mt-2 w-full rounded-control border border-os-border bg-white px-2.5 py-2 text-[0.8125rem] text-os-ink outline-none placeholder:text-os-ink-4 focus:border-os-ink-3"
                    />
                    {noteErr && (
                      <p className="mt-1 text-[0.75rem] text-os-red" data-testid="docs-md-note-error">
                        {noteErr}
                      </p>
                    )}
                    <div className="mt-2 flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setSelDraft(null);
                          setNoteDraft("");
                          setNoteErr("");
                        }}
                        data-testid="docs-md-note-cancel"
                        className="rounded-control border border-os-border bg-white px-2.5 py-1 text-[0.75rem] text-os-ink hover:bg-os-wash"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={saveNote}
                        disabled={noteBusy || !noteDraft.trim()}
                        data-testid="docs-md-note-save"
                        className="rounded-control border border-os-ink bg-os-wash px-2.5 py-1 text-[0.75rem] font-medium text-os-ink hover:bg-os-border disabled:opacity-50"
                      >
                        Save note
                      </button>
                    </div>
                  </div>
                )}
                {openThreadRow && (
                  <ThreadPanel
                    thread={openThreadRow}
                    onClose={() => setOpenThreadId(null)}
                    onReply={sendNoteReply}
                    onToggleResolve={toggleNoteResolve}
                  />
                )}
              </div>
            )}

            {tab === "history" && (
              <div
                className="min-h-0 flex-1 overflow-y-auto px-6 py-6"
                data-testid="docs-history"
              >
                {versions.length === 0 && (
                  <p
                    className="text-[0.875rem] text-os-ink-3"
                    data-testid="docs-history-empty"
                  >
                    No versions have been recorded for this document yet.
                  </p>
                )}
                {versions.length > 0 && (
                  <>
                    {/* History clarity: the tab lists COMMITS to this file — a
                        commit message may name other documents when one change
                        touched several files. The badges separate Jeff's own
                        reader edits from workspace-sync commits. */}
                    <p
                      className="mb-3 text-[0.75rem] leading-[1.5] text-os-ink-3"
                      data-testid="docs-history-note"
                    >
                      Workspace history — each entry records a committed change
                      to this file. Other documents may be named in messages
                      when one commit touched several files.
                    </p>
                    <ul
                      className="flex flex-col gap-1"
                      data-testid="docs-history-list"
                    >
                    {versions.map((v) => (
                      <li key={v.hash}>
                        <button
                          type="button"
                          onClick={() =>
                            setCompare(compare === v.hash ? "" : v.hash)
                          }
                          data-testid={`docs-version-${v.hash}`}
                          className={cn(
                            "flex w-full items-baseline gap-3 rounded-control border px-3 py-2 text-left",
                            compare === v.hash
                              ? "border-os-ink-3 bg-os-wash"
                              : "border-os-border hover:bg-os-wash",
                          )}
                        >
                          <span className="font-mono text-[0.625rem] text-os-ink-4">
                            {String(v.hash || "").slice(0, 7)}
                          </span>
                          <span className="min-w-0 flex-1 truncate text-[0.875rem] text-os-ink">
                            {v.cleanMessage}
                          </span>
                          {v.editKind === "edit" && (
                            <span
                              data-testid="history-badge-edit"
                              className="shrink-0 rounded-control bg-os-ink-3 px-1.5 py-0.5 font-mono text-[0.625rem] text-white"
                            >
                              in-app edit
                            </span>
                          )}
                          {v.editKind === "sync" && (
                            <span
                              data-testid="history-badge-sync"
                              className="shrink-0 rounded-control border border-os-border px-1.5 py-0.5 font-mono text-[0.625rem] text-os-ink-4"
                            >
                              workspace sync
                            </span>
                          )}
                          {v.stats && (
                            <span
                              data-testid="history-headline"
                              className="inline-block w-full font-mono text-[0.625rem] text-os-ink-4"
                            >
                              {v.stats.text}
                            </span>
                          )}
                          <span className="shrink-0 font-mono text-[0.625rem] text-os-ink-4">
                            {stampText(v.date)}
                          </span>
                        </button>
                      </li>
                    ))}
                    </ul>
                  </>
                )}

                {compare && (
                  <div className="mt-5" data-testid="docs-diff">
                    <div className="mb-2 flex items-center gap-2">
                      <Label>Compared with the current text</Label>
                      <button
                        type="button"
                        onClick={() => setCompare("")}
                        data-testid="docs-diff-close"
                        className="inline-flex items-center gap-1 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-3 hover:text-os-ink"
                      >
                        <X className="h-3 w-3" strokeWidth={2} />
                        close
                      </button>
                    </div>
                    {diff ? (
                      <pre className="overflow-x-auto whitespace-pre-wrap rounded-card border border-os-border bg-os-wash p-4 font-mono text-[0.75rem] leading-[1.5] text-os-ink">
                        {diff}
                      </pre>
                    ) : (
                      <p className="text-[0.875rem] text-os-ink-3">
                        No difference against the current text.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {tab === "pdf" && (
              <div className="min-h-0 flex-1" data-testid="docs-pdf">
                <PdfViewer docId={doc.id} title={doc.title} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ── the page ──────────────────────────────────────────────────────────────

export default function DocsPage() {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [lanes, setLanes] = useState({});
  const [total, setTotal] = useState(0);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const lane = searchParams.get("lane") || "";
  // P3.2 Phase B: the brand facet lives in the URL beside q and lane, so a
  // filtered hub is linkable and the back button walks out of it.
  const brand = searchParams.get("brand") || "";
  const openId = params.id || null;
  const first = useRef(true);

  // The search box talks to the URL, so a search can be linked to and the
  // back button works.
  //
  // The URL is only rewritten when it actually differs: useSearchParams
  // hands back a fresh object on every render, so writing unconditionally
  // would navigate in a loop and the list's fetch would be cancelled and
  // restarted forever.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (query) next.set("q", query);
    else next.delete("q");
    if (lane) next.set("lane", lane);
    else next.delete("lane");
    if (brand) next.set("brand", brand);
    else next.delete("brand");
    const wanted = next.toString();
    const current = searchParams.toString();
    if (wanted === current) return;
    setSearchParams(next, { replace: true });
  }, [query, lane, brand, searchParams, setSearchParams]);

  // Picking a lane is a search too — it narrows the same list, so it
  // belongs in the URL beside the query. The brand facet behaves the same.
  const setLane = useCallback(
    (next) => {
      const params = new URLSearchParams(searchParams);
      if (next) params.set("lane", next);
      else params.delete("lane");
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );
  const setBrand = useCallback(
    (next) => {
      const params = new URLSearchParams(searchParams);
      if (next) params.set("brand", next);
      else params.delete("brand");
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // P3.2 Phase B: the brand summary for the chip row — fetched once per
  // visit to the list view, exactly what GET /api/docs/brands answers
  // ([{name, count, pdfCount}], count desc / name asc). Empty [] renders no
  // row; a failed call is silent (the lanes' row still works).
  const [brands, setBrands] = useState([]);
  useEffect(() => {
    if (openId) return;
    let live = true;
    fetchDocBrands()
      .then((rows) => {
        if (live) setBrands(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (live) setBrands([]);
      });
    return () => {
      live = false;
    };
  }, [openId]);

  // The rail's library fetch. It runs on first paint ALWAYS — with a
  // document open too (the rail used to early-return on openId and stayed
  // empty for the whole session once a doc was on screen). While reading, a
  // query or lane change still refetches the list; the debounce is softer
  // (300ms) so typing while reading doesn't hammer the backend. A refresh
  // in flight never clears the rail: `items` only changes when a response
  // lands, so the previous list stays visible while the new one loads.
  useEffect(() => {
    let live = true;
    setLoading(true);
    const t = setTimeout(
      () => {
        fetchDocs({ q: query, lane, brand, limit: 200 })
          .then((body) => {
            if (!live) return;
            setItems(body.items || []);
            setLanes(body.lanes || {});
            setTotal(body.total || 0);
            setMeta(body.meta || {});
            setError("");
            setLoading(false);
          })
          .catch((e) => {
            if (!live) return;
            setError(e.message || "The library isn't answering right now.");
            setLoading(false);
          });
      },
      query ? 300 : 0,
    );
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [query, lane, brand]);

  // The first paint must not flash the list before the URL catches up.
  useEffect(() => {
    if (first.current) {
      first.current = false;
    }
  }, []);

  // ── DOC-P32: the rail that reads with you ───────────────────────────────
  // Recently viewed: the ids the reader opened this session, newest first,
  // no dupes, persisted in sessionStorage ("docs-recent") so a remount keeps
  // the trail. Unresolved ids are dropped at render time (the doc may be
  // gone from the list), never stored.
  const [recentIds, setRecentIds] = useState(() => {
    try {
      const parsed = JSON.parse(sessionStorage.getItem("docs-recent") || "[]");
      return Array.isArray(parsed) ? parsed.filter((x) => typeof x === "string") : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    if (!openId) return;
    setRecentIds((prev) => {
      if (prev[0] === openId) return prev;
      const next = [openId, ...prev.filter((id) => id !== openId)].slice(0, 6);
      return next;
    });
  }, [openId]);

  useEffect(() => {
    try {
      sessionStorage.setItem("docs-recent", JSON.stringify(recentIds));
    } catch {
      // Storage refusing (private mode / quota) must never break the rail.
    }
  }, [recentIds]);

  // Same folder: the open doc's directory prefix (its path minus the last
  // segment), matched against the ALREADY-FETCHED list — no new backend
  // call. Ordered by title, capped at 12, and the section stays hidden
  // until at least 2 siblings exist (a lone neighbor is not a section).
  const siblings = useMemo(() => {
    const open = items.find((it) => it.id === openId);
    if (!open || !open.path) return [];
    const parts = String(open.path).split("/");
    parts.pop();
    const dir = parts.join("/");
    if (!dir) return [];
    const picked = items
      .filter((it) => it.id !== openId && String(it.path || "").startsWith(`${dir}/`))
      .sort((a, b) =>
        String(a.title || "").localeCompare(String(b.title || "")),
      )
      .slice(0, 12);
    return picked.length >= 2 ? picked : [];
  }, [items, openId]);

  // The visible Recently viewed rows: stored ids resolved against fetched
  // items, the open doc excluded, up to 6, newest first.
  const recent = useMemo(() => {
    const byId = new Map(items.map((it) => [it.id, it]));
    const out = [];
    for (const id of recentIds) {
      if (id === openId) continue;
      const it = byId.get(id);
      if (it) out.push(it);
    }
    return out.slice(0, 6);
  }, [recentIds, items, openId]);

  const openDoc = useCallback(
    (id) => {
      const next = new URLSearchParams(searchParams);
      setSearchParams(next, { replace: true });
      navigate(
        `/docs/${encodeURIComponent(id)}${next.toString() ? `?${next}` : ""}`,
      );
    },
    [navigate, searchParams, setSearchParams],
  );

  const backToList = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    navigate(`/docs${next.toString() ? `?${next}` : ""}`);
  }, [navigate, searchParams]);

  return (
    <div
      data-testid="docs-page"
      className="h-full w-full font-inter text-os-ink"
    >
      <div className="mx-auto flex h-full min-h-0 max-w-[100rem] flex-col px-4 py-5 lg:px-8">
        {!openId && (
          <header className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
            <div>
              <h1 className="text-[1.5rem] font-semibold tracking-[-0.01em]">
                Documents
              </h1>
              <p className="mt-1 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-4">
                {total} in the library
                {meta.ingested_at
                  ? ` · synced ${stampText(meta.ingested_at)}`
                  : ""}
              </p>
            </div>
          </header>
        )}

        <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-[22rem_minmax(0,1fr)]">
          <Card
            quiet
            className={cn(
              "min-h-0 overflow-hidden",
              openId ? "hidden lg:flex lg:flex-col" : "flex flex-col",
            )}
          >
            <DocList
              items={items}
              lanes={lanes}
              activeLane={lane}
              onLane={setLane}
              activeBrand={brand}
              onBrand={setBrand}
              brands={brands}
              query={query}
              onQuery={setQuery}
              onOpen={openDoc}
              loading={loading}
              error={error}
              total={total}
              openId={openId}
              siblings={siblings}
              recent={recent}
            />
          </Card>

          <Card
            quiet
            className={cn(
              "min-h-0 overflow-hidden",
              openId ? "flex flex-col" : "hidden lg:flex lg:flex-col",
            )}
          >
            {openId ? (
              <DocReader
                docId={openId}
                onBack={backToList}
                onOpenDoc={openDoc}
              />
            ) : (
              <div
                className="flex flex-1 flex-col items-center justify-center gap-2 px-6 py-10 text-center"
                data-testid="docs-placeholder"
              >
                <History className="h-5 w-5 text-os-ink-4" strokeWidth={1.5} />
                <p className="text-[0.875rem] text-os-ink-3">
                  Pick a document to read it here.
                </p>
                <p className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-4">
                  every document carries its own history
                </p>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
